import JoinQr from './JoinQr'
import type { DraftState } from '../../types/protocol'

// Pre-draft state: the QR is the whole point of the screen, so it gets the
// left half at 28vw, and the team list doubles as the "who still needs to
// join" checklist the commissioner would otherwise read off a phone.

interface Props {
  draft: DraftState
}

export default function LobbyView({ draft }: Props) {
  // Between scheduling a draw and drawing it there is no draft order, but
  // add_team has already numbered every team 1,2,3... in creation order — so
  // without this the TV shows a complete, confident, meaningless order for the
  // whole countdown and then silently renumbers itself. The board still shows
  // nothing *about* the lottery; it just stops answering a question it can't.
  //
  // No status check: Board only renders LobbyView while status is 'setup', and
  // the sweeper's query is scoped to setup too, so the two agree by
  // construction.
  const orderPending = draft.lottery_at !== null && draft.lottery_revealed_at === null
  const claimed = draft.teams.filter((team) => team.claimed).length

  return (
    <div className="flex min-h-0 flex-1 items-center gap-[4vw] px-[4vw] py-[3vh]">
      <div className="flex flex-none flex-col items-center gap-[2vh]">
        <JoinQr code={draft.join_code} sizeClass="w-[28vw] h-[28vw]" />
        <p className="text-board-hero font-display leading-none font-bold tracking-[0.12em] text-signal tabular-nums">
          {draft.join_code}
        </p>
        <p className="text-board-title font-display font-semibold text-ink-mid">Scan to join</p>
      </div>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-[1.5vh]">
        <p className="text-board-title font-display font-bold text-ink-mid">
          {claimed} of {draft.teams.length} teams in
        </p>
        <div className="grid min-h-0 flex-1 auto-rows-fr grid-cols-2 gap-[1vh] overflow-hidden">
          {draft.teams.map((team) => (
            <div
              key={team.id}
              className={`flex min-w-0 items-center gap-[1vw] overflow-hidden rounded-[1vh] px-[1.2vw] py-[1vh] ${
                team.claimed
                  ? 'bg-pos-rb text-pos-ink shadow-[inset_0_0.2vh_0_rgb(255_255_255/0.2)]'
                  : 'bg-surface-1 text-ink-lo shadow-panel'
              }`}
            >
              <span className="text-board-team font-display flex-none font-bold tabular-nums opacity-70">
                {orderPending ? '🔒' : (team.draft_position ?? '-')}
              </span>
              <span className="min-w-0 flex-1 truncate">
                <span className="text-board-team font-display block truncate font-bold">
                  {team.name}
                </span>
                <span className="text-board-sub block truncate font-medium opacity-80">
                  {team.owner_name ?? 'Waiting…'}
                </span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
